/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { AccordionCard, FCWithFragments } from '@modernloop/shared/components';
import { getLocalTimezone, getUniqueDays } from '@modernloop/shared/datetime';
import { PlusIcon, SendIcon } from '@modernloop/shared/icons';
import { Button, Paper, Stack, Theme, Tooltip, Typography } from '@mui/material';
import { differenceInMinutes, parseISO } from 'date-fns';

import {
  BaseCandidateAvailabilitySection_ApplicationFragment,
  BaseCandidateAvailabilitySection_AvailabilityFragment,
  BaseCandidateAvailabilitySection_AvailabilityRequestFragment,
  BaseCandidateAvailabilitySection_JobStageSettingsV2Fragment,
  Maybe,
} from 'src/generated/mloop-graphql';

import DurationLabel from 'src/components/DurationLabel';
import useDurationLabel from 'src/components/DurationLabel/useDurationLabel';
import Link from 'src/components/Link';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import { useOrgAtsService } from 'src/hooks/atsService';
import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useIsApplicationRejected from 'src/hooks/useIsApplicationRejected';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { formatDurationFromMinutes } from 'src/utils/datetime/Format';

import CandidateAvailabilityOptionsModal from 'src/views-new/CandidateAvailabilityOptions/CandidateAvailabilityOptionsModalWrapper';
import { CandidateAvailabilityOptionsModalPage } from 'src/views-new/CandidateAvailabilityOptions/types';

import CandidateAvailability from './CandidateAvailability';
import CandidateAvailabilityStatus from './CandidateAvailabilityStatus';
import EditCandidateAvailabilityModal from './EditCandidateAvailabilityModal';
import ResetAvailabilityModal from './ResetAvailabilityModal';

type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  jobId: string;
  interviewPlanId?: string;
  taskId: string;
  candidateTimezone?: string;
  coordinatorId?: string;
  lastApplicationStageCreatedAt?: IsoTimestamp;
  isCurrentAts: boolean;
  showStatusOnly?: boolean;
  readonly?: boolean;
  jobStageName: string;
  selfScheduleRequestId?: string;
  options?: CandidateAvailabilityOptions;
  availabilityRequestedAfterSchedule: boolean;
  availabilityReceivedAfterSchedule: boolean;
};

type Fragments = {
  application: BaseCandidateAvailabilitySection_ApplicationFragment;
  availability: BaseCandidateAvailabilitySection_AvailabilityFragment | undefined;
  availabilityRequest?: Maybe<BaseCandidateAvailabilitySection_AvailabilityRequestFragment>;
  jobStageSettingsV2: BaseCandidateAvailabilitySection_JobStageSettingsV2Fragment | undefined;
};

const BaseCandidateAvailabilitySection: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  jobId,
  interviewPlanId,
  candidateTimezone,
  taskId,
  jobStageName,
  selfScheduleRequestId,
  lastApplicationStageCreatedAt,
  isCurrentAts,
  showStatusOnly,
  readonly,
  jobStageSettingsV2,
  options,
  availabilityRequestedAfterSchedule,
  availabilityReceivedAfterSchedule,
  application,
  availability,
  availabilityRequest,
}) => {
  const { t } = useTranslation();
  const localTimezone = getLocalTimezone();
  const candidateTZ = candidateTimezone || availabilityRequest?.timezone || localTimezone;
  const [showAvailabilityOptions, setShowAvailabilityOptions] = useState(false);
  const [showEditAvailabilityModal, setShowEditAvailabilityModal] = useState(false);
  const [showResetAvailabilityModal, setShowResetAvailabilityModal] = useState(false);
  const [candidateAvailabilityOptionsModalPage, setCandidateAvailabilityOptionsModalPage] =
    useState<CandidateAvailabilityOptionsModalPage>();
  const [disableEditOptions, setDisableEditOptions] = useState(false);

  const showApplicationRejected = useIsApplicationRejected(application.status);

  const atsService = useOrgAtsService();
  const activityFeedRefetch = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const isInterviewScheduledAfterRequestCreated = useMemo(() => {
    return (
      lastApplicationStageCreatedAt &&
      availabilityRequest?.createdAt &&
      parseISO(lastApplicationStageCreatedAt).getTime() > parseISO(availabilityRequest.createdAt).getTime()
    );
  }, [availabilityRequest?.createdAt, lastApplicationStageCreatedAt]);

  const availabilityPresent = Boolean(
    availability?.candidateEnteredAvailability?.length || availability?.rcEnteredAvailability?.length
  );

  const toggleShowAvailabilityOptions = useCallback(
    () => setShowAvailabilityOptions(!showAvailabilityOptions),
    [showAvailabilityOptions]
  );

  const toggleShowEditAvailabilityModal = useCallback(
    () => setShowEditAvailabilityModal(!showEditAvailabilityModal),
    [showEditAvailabilityModal]
  );

  const toggleShowResetAvailabilityModal = useCallback(
    () => setShowResetAvailabilityModal(!showResetAvailabilityModal),
    [showResetAvailabilityModal]
  );

  const durationLabel = useDurationLabel(
    availability?.updatedAt ? assertIsoTimestamp(availability.updatedAt) : undefined
  );

  const handleCreateAvailabilityRequest = useCallback(async () => {
    if (taskId && availabilityPresent) {
      toggleShowResetAvailabilityModal();
    } else {
      await refetchScheduleTaskQuery();
      toggleShowAvailabilityOptions();
    }
  }, [
    availabilityPresent,
    taskId,
    toggleShowAvailabilityOptions,
    toggleShowResetAvailabilityModal,
    refetchScheduleTaskQuery,
  ]);

  const refreshScheduleTaskQuery = useCallback(() => {
    if (!taskId) {
      return;
    }
    refetchScheduleTaskQuery();
    activityFeedRefetch();
  }, [activityFeedRefetch, refetchScheduleTaskQuery, taskId]);

  const modalsJsx = (
    <>
      {showEditAvailabilityModal && (
        <EditCandidateAvailabilityModal
          applicationId={applicationId}
          jobStageId={jobStageId}
          taskId={taskId}
          candidateTimezone={candidateTZ}
          availability={availability}
          onClose={() => {
            toggleShowEditAvailabilityModal();
            setCandidateAvailabilityOptionsModalPage(CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION);
            refreshScheduleTaskQuery();
          }}
        />
      )}
      {showAvailabilityOptions && (
        <CandidateAvailabilityOptionsModal
          isSelfScheduleRequested={Boolean(selfScheduleRequestId)}
          applicationId={applicationId}
          candidateId={candidateId}
          jobStageId={jobStageId}
          jobId={jobId}
          jobStageSettingsV2={jobStageSettingsV2}
          interviewPlanId={interviewPlanId}
          taskId={taskId}
          taskOptions={taskId ? options : undefined}
          timezone={localTimezone}
          initialPage={candidateAvailabilityOptionsModalPage}
          disableEditOptions={disableEditOptions}
          onSaveSuccess={refreshScheduleTaskQuery}
          onClose={() => {
            toggleShowAvailabilityOptions();
            setCandidateAvailabilityOptionsModalPage(undefined);
            setDisableEditOptions(false);
          }}
          onDelete={refreshScheduleTaskQuery}
        />
      )}
      {showResetAvailabilityModal && (
        <ResetAvailabilityModal
          applicationId={applicationId}
          jobStageId={jobStageId}
          taskId={taskId}
          candidateTimezone={candidateTZ}
          onClose={toggleShowResetAvailabilityModal}
          onConfirm={() => {
            toggleShowResetAvailabilityModal();
            toggleShowAvailabilityOptions();
          }}
        />
      )}
    </>
  );

  let subTitle = t('hiring-dashboard-candidates:candidates.availability.noAvailability');
  if (availabilityRequest?.externalId) {
    if (!availabilityPresent) {
      subTitle = t('hiring-dashboard-candidates:candidates.availability.noAvailabilityRequestOpen');
    }
  }

  if (availabilityPresent) {
    const combinedAvailabilities = [
      ...(availability?.candidateEnteredAvailability || []),
      ...(availability?.rcEnteredAvailability || []),
    ];
    const totalDurationInMinutes = combinedAvailabilities.reduce((prevDuration, a) => {
      return prevDuration + differenceInMinutes(parseISO(a.endAt), parseISO(a.startAt));
    }, 0);
    const totalDays = getUniqueDays(
      combinedAvailabilities.map((a) => assertIsoTimestamp(a.startAt)),
      candidateTimezone || getLocalTimezone()
    );

    let addedBy = '';
    if (availability?.candidateEnteredAvailability?.length) {
      addedBy = t('hiring-dashboard-candidates:candidates.availability.addedByCandidate');
    } else if (availability?.lastUpdatedByEmployee?.fullName) {
      addedBy = t('hiring-dashboard-candidates:candidates.availability.addedByEmployee', {
        employeeName: availability?.lastUpdatedByEmployee.fullName,
      });
    }

    const requestIsOpen =
      !availability?.candidateEnteredAvailability?.length &&
      availability?.lastUpdatedByEmployee?.id &&
      availabilityRequest?.externalId
        ? t('hiring-dashboard-candidates:candidates.availability.requestIsOpen')
        : '';

    subTitle = t('hiring-dashboard-candidates:candidates.availability.durationOverDays', {
      duration: formatDurationFromMinutes(totalDurationInMinutes),
      count: totalDays.length,
      addedBy,
      durationLabel,
      requestIsOpen,
    });
  }

  const getAvailabilityRequestActions = () => {
    const tooltipTitle = showApplicationRejected
      ? t('hiring-dashboard-candidates:candidates.availability.applicationRejected')
      : '';
    return taskId && !selfScheduleRequestId && availabilityRequest?.externalId ? (
      <Stack alignItems="center" direction="row" sx={{ flexWrap: 'wrap', gap: 1, paddingTop: 1 }}>
        <Tooltip title={tooltipTitle}>
          <span>
            <Button
              data-testid="candidate-availability-re-share-request-link-button"
              size="small"
              disabled={showApplicationRejected}
              onClick={() => {
                setCandidateAvailabilityOptionsModalPage(CandidateAvailabilityOptionsModalPage.SAVE_SUCCESS);
                setShowAvailabilityOptions(true);
              }}
              color="primary"
            >
              {t('hiring-dashboard-candidates:candidates.availability.reShareRequestLink')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={tooltipTitle}>
          <span>
            <Button
              size="small"
              disabled={showApplicationRejected}
              onClick={() => {
                setCandidateAvailabilityOptionsModalPage(CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION);
                setShowAvailabilityOptions(true);
              }}
            >
              {t('hiring-dashboard-candidates:candidates.availability.editRequest')}
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          onClick={() => {
            setCandidateAvailabilityOptionsModalPage(
              CandidateAvailabilityOptionsModalPage.DELETE_AVAILABILITY_CONFIRMATION
            );
            setShowAvailabilityOptions(true);
          }}
        >
          {t('hiring-dashboard-candidates:candidates.availability.cancelRequest')}
        </Button>
      </Stack>
    ) : null;
  };

  // In task requirement section we only want to show CandidateAvailabilityStatus when there is a request open
  const statusJsx = isCurrentAts && !readonly && (
    <CandidateAvailabilityStatus
      taskId={taskId}
      application={application}
      jobStageName={jobStageName}
      candidateTimezone={candidateTimezone}
      options={options}
      availabilityRequest={availabilityRequest}
      availability={availability}
      selfScheduleOptionsId={selfScheduleRequestId}
      lastApplicationStageCreatedAt={lastApplicationStageCreatedAt}
      onRequestAvailability={() => {
        setCandidateAvailabilityOptionsModalPage(CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION);
        setDisableEditOptions(false);
        toggleShowAvailabilityOptions();
      }}
      onReShareAvailabilityRequest={() => {
        setCandidateAvailabilityOptionsModalPage(CandidateAvailabilityOptionsModalPage.SAVE_SUCCESS);
        setDisableEditOptions(true);
        toggleShowAvailabilityOptions();
      }}
      onDeleteAvailabilityRequest={() => {
        setCandidateAvailabilityOptionsModalPage(
          CandidateAvailabilityOptionsModalPage.DELETE_AVAILABILITY_CONFIRMATION
        );
        toggleShowAvailabilityOptions();
      }}
      availabilityRequestActionsJSX={getAvailabilityRequestActions()}
    />
  );

  const hasCandidateSubmittedAvailabilityAfterRequestCreated =
    availability?.candidateUpdatedAt &&
    availabilityRequest?.createdAt &&
    parseISO(availability.candidateUpdatedAt).getTime() > parseISO(availabilityRequest.createdAt).getTime();

  const availabilityPresentWithNoOptionsForTask = taskId && availabilityPresent && !availabilityRequest?.externalId;

  if (showStatusOnly) {
    if (isInterviewScheduledAfterRequestCreated || hasCandidateSubmittedAvailabilityAfterRequestCreated) {
      return <></>;
    }
    // Don't render status if there is no candidate avaialbility request by candidate
    if (
      !availabilityRequest?.externalId ||
      (availability?.candidateEnteredAvailability?.length && hasCandidateSubmittedAvailabilityAfterRequestCreated)
    ) {
      return <></>;
    }

    return (
      <>
        {statusJsx}
        {modalsJsx}
      </>
    );
  }

  const jsx = (
    <Stack spacing={1}>
      {availabilityPresent && (
        <CandidateAvailability
          availability={availability}
          availabilityRequest={availabilityRequest || undefined}
          candidateTimezone={candidateTimezone}
          readonly={readonly}
          showEditIcon={!taskId}
          onAddAvailability={toggleShowEditAvailabilityModal}
        />
      )}

      {(!availabilityRequest?.externalId ||
        isInterviewScheduledAfterRequestCreated ||
        hasCandidateSubmittedAvailabilityAfterRequestCreated ||
        taskId) &&
        statusJsx}

      {taskId && availabilityRequestedAfterSchedule && !availabilityReceivedAfterSchedule && availabilityRequest && (
        <Paper
          sx={{
            borderRadius: (theme: Theme) => theme.spacing(1.5),
            padding: (theme: Theme) =>
              `${theme.spacing(2.5)} ${theme.spacing(1.5)} ${theme.spacing(2.5)} ${theme.spacing(2)}`,
            backgroundColor: (theme) => theme.palette.warning.tint,
          }}
        >
          <Stack alignItems="left" direction="row" justifyContent="left" spacing={1}>
            <SendIcon color="warning" />
            {availabilityRequest?.lastCandidateCommunicationSentAt === null ? (
              <Stack alignItems="left" justifyContent="space-between">
                <Typography variant="subtitle1">
                  {t('hiring-dashboard-candidates:candidates.availability.waitingToSendRequest')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('hiring-dashboard-candidates:candidates.availability.requestCreatedBy', {
                    creatorName: availabilityRequest?.creator?.fullName,
                  })}
                  <DurationLabel
                    timestamp={assertIsoTimestamp(availabilityRequest?.updatedAt as string)}
                    typographyProps={{
                      variant: 'body2',
                      fontWeight: 400,
                      color: 'text.secondary',
                      noWrap: true,
                      component: 'span',
                    }}
                  />
                  .
                </Typography>
                {getAvailabilityRequestActions()}
              </Stack>
            ) : (
              <Stack alignItems="left" justifyContent="space-between">
                <Typography variant="subtitle1">
                  {t('hiring-dashboard-candidates:candidates.availability.waitingForCandidate')}
                </Typography>
                {availabilityRequest.lastCandidateCommunicationSentAt && (
                  <Typography variant="body2" color="text.secondary">
                    {t('hiring-dashboard-candidates:candidates.availability.linkSentToCandidate', {
                      creatorName: availabilityRequest?.creator?.fullName,
                    })}
                    <DurationLabel
                      timestamp={assertIsoTimestamp(availabilityRequest.lastCandidateCommunicationSentAt)}
                      typographyProps={{
                        variant: 'body2',
                        fontWeight: 400,
                        color: 'text.secondary',
                        noWrap: true,
                        component: 'span',
                      }}
                    />
                    .
                  </Typography>
                )}
                {getAvailabilityRequestActions()}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
      {taskId && availabilityReceivedAfterSchedule && availabilityRequest && (
        <Paper
          color="pending"
          sx={{
            borderRadius: (theme: Theme) => theme.spacing(1.5),
            padding: (theme: Theme) =>
              `${theme.spacing(2.5)} ${theme.spacing(1.5)} ${theme.spacing(2.5)} ${theme.spacing(2)}`,
          }}
        >
          <Stack alignItems="left" direction="row" justifyContent="left" spacing={1}>
            <SendIcon color="warning" />
            <Stack alignItems="left" justifyContent="space-between">
              <Typography variant="subtitle1">
                {t('hiring-dashboard-candidates:candidates.availability.requestStillOpen')}
              </Typography>
              {availabilityRequest.lastCandidateCommunicationSentAt && (
                <Typography variant="body2" color="text.secondary">
                  {t('hiring-dashboard-candidates:candidates.availability.linkSentToCandidate', {
                    creatorName: availabilityRequest?.creator?.fullName,
                  })}
                  <DurationLabel
                    timestamp={assertIsoTimestamp(availabilityRequest.lastCandidateCommunicationSentAt)}
                    typographyProps={{
                      variant: 'body2',
                      fontWeight: 400,
                      color: 'text.secondary',
                      noWrap: true,
                      component: 'span',
                    }}
                  />
                  .
                </Typography>
              )}
              {getAvailabilityRequestActions()}
            </Stack>
          </Stack>
        </Paper>
      )}

      {!taskId && !selfScheduleRequestId && (
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          {!availabilityPresent && (
            <Button
              disabled={readonly}
              startIcon={<PlusIcon color={readonly ? 'disabled' : undefined} />}
              onClick={toggleShowEditAvailabilityModal}
            >
              {t('hiring-dashboard-candidates:candidates.availability.addAvailability')}
            </Button>
          )}
          {availabilityPresentWithNoOptionsForTask && (
            <Button onClick={toggleShowAvailabilityOptions}>
              {t('hiring-dashboard-candidates:candidates.availability.requestNewAvailability')}
            </Button>
          )}
          {atsService?.isAtsImportAvailabilityEnabled && (
            <Link
              color="info"
              href={HelpCenterUrl.MISSING_CANDIDATE_AVAILABILITY}
              style={{ marginLeft: 'auto', textDecoration: 'none' }}
              target="_blank"
            >
              {t('hiring-dashboard-candidates:candidates.availability.missingAvailability')}
            </Link>
          )}
        </Stack>
      )}

      {taskId && !selfScheduleRequestId && (
        <Stack alignItems="center" direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {!availabilityRequest?.externalId && (
            <Tooltip
              title={
                showApplicationRejected
                  ? t('hiring-dashboard-candidates:candidates.availability.applicationRejected')
                  : ''
              }
            >
              <span>
                <Button
                  data-testid="schedule-task-candidate-availability-request-button"
                  variant={availabilityPresent ? undefined : 'contained'}
                  color={availabilityPresent ? undefined : 'primary'}
                  size="small"
                  disabled={showApplicationRejected}
                  onClick={handleCreateAvailabilityRequest}
                >
                  {availabilityPresent
                    ? t('hiring-dashboard-candidates:candidates.availability.requestNewAvailability')
                    : t('hiring-dashboard-candidates:candidates.availability.sendAvailabilityRequest')}
                </Button>
              </span>
            </Tooltip>
          )}

          <Button size="small" onClick={toggleShowEditAvailabilityModal}>
            {availabilityPresent
              ? t('hiring-dashboard-candidates:candidates.availability.editAvailability')
              : t('hiring-dashboard-candidates:candidates.availability.addAvailabilityManually')}
          </Button>
          {atsService?.isAtsImportAvailabilityEnabled && (
            <Button
              size="small"
              onClick={() => {
                window.open(HelpCenterUrl.MISSING_CANDIDATE_AVAILABILITY, '_blank');
              }}
            >
              {t('hiring-dashboard-candidates:candidates.availability.help')}
            </Button>
          )}
        </Stack>
      )}
      {modalsJsx}
    </Stack>
  );

  return (
    <AccordionCard
      title={
        taskId ? <Typography>{subTitle}</Typography> : t('hiring-dashboard-candidates:candidates.availability.title')
      }
      subTitle={taskId ? undefined : subTitle}
    >
      {jsx}
    </AccordionCard>
  );
};

BaseCandidateAvailabilitySection.fragments = {
  application: gql`
    ${CandidateAvailabilityStatus.fragments.application}
    fragment BaseCandidateAvailabilitySection_application on Application {
      id
      status
      ...CandidateAvailabilityStatus_application
    }
  `,
  availability: gql`
    ${CandidateAvailability.fragments.availability}
    ${EditCandidateAvailabilityModal.fragments.availability}
    fragment BaseCandidateAvailabilitySection_availability on CandidateAvailability {
      id
      lastUpdatedByEmployee {
        id
        fullName
      }
      candidateUpdatedAt
      ...CandidateAvailability_availability
      ...EditCandidateAvailabilityModal_availability
    }
  `,
  availabilityRequest: gql`
    ${CandidateAvailabilityStatus.fragments.availabilityRequest}
    ${CandidateAvailability.fragments.availabilityRequest}
    fragment BaseCandidateAvailabilitySection_availabilityRequest on CandidateAvailabilityOptions {
      id
      createdAt
      updatedAt
      timezone
      externalId
      creator {
        id
        fullName
      }
      lastCandidateCommunicationSentAt
      ...CandidateAvailabilityStatus_availabilityRequest
      ...CandidateAvailability_availabilityRequest
    }
  `,
  jobStageSettingsV2: gql`
    ${CandidateAvailabilityOptionsModal.fragments.jobStageSettingsV2}
    fragment BaseCandidateAvailabilitySection_JobStageSettingsV2 on JobStageSettingsV2 {
      ...CandidateAvailabilityOptionsModalWrapper_jobStageSettingsV2
    }
  `,
};

export default BaseCandidateAvailabilitySection;
